import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";
import { API } from "../../context/AuthContext";
import { TRACKS } from "./skins";

const inputCls = "w-full bg-gray-950 border border-white/10 rounded-lg px-3 py-2 text-xs text-gray-100 focus:border-[#f59e0b]/50 focus:outline-none";

export default function AddEventDialog({ caseId, onAdded, onClose }) {
  const [form, setForm] = useState({ date: "", title: "", description: "", status: "completed", party: "neutral", tracks: ["procedural"] });
  const [saving, setSaving] = useState(false);

  const toggleTrack = (key) => {
    setForm((f) => {
      const has = f.tracks.includes(key);
      const next = has ? f.tracks.filter((t) => t !== key) : [...f.tracks, key];
      return { ...f, tracks: next.length ? next : f.tracks };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.title.trim()) {
      toast.error("Date and title are required.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/events`, { ...form, case_id: caseId });
      onAdded(res.data);
      toast.success("Event added to the timeline!");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add the event.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="add-event-dialog">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#131f3a] border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-4 shadow-2xl">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide font-display">Add Event Manually</h3>
          <button type="button" onClick={onClose} data-testid="add-event-close-btn" className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-gray-400">Flux missed something? Drop it straight onto the timeline.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[10px] font-mono text-gray-400 uppercase">Date</label>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="add-event-date-input" />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-mono text-gray-400 uppercase">Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="add-event-status-select">
              <option value="completed">Completed</option>
              <option value="pending">Pending / Upcoming</option>
              <option value="alert">Alert / Incident</option>
            </select>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-mono text-gray-400 uppercase">Title</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={150} placeholder="e.g., Review hearing held" className={inputCls} data-testid="add-event-title-input" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-mono text-gray-400 uppercase">Description</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={600} rows={2} placeholder="What happened?" className={inputCls} data-testid="add-event-desc-input" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-mono text-gray-400 uppercase">Party</label>
          <select value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} className={inputCls} data-testid="add-event-party-select">
            <option value="neutral">Neutral / Both</option>
            <option value="petitioner">Petitioner</option>
            <option value="respondent">Respondent</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-mono text-gray-400 uppercase">Tracks</label>
          <div className="flex flex-wrap gap-1.5">
            {TRACKS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => toggleTrack(t.key)}
                data-testid={`add-event-track-${t.key}`}
                className={`text-[10px] font-mono border px-2 py-1 rounded-lg transition-colors duration-200 ${form.tracks.includes(t.key) ? t.cls : "border-white/10 text-gray-500 hover:text-gray-300"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="add-event-submit-btn" className="w-full bg-[#f59e0b] hover:bg-[#fbbf24] text-gray-950 font-bold text-xs py-2.5 rounded-lg transition-colors duration-200 disabled:opacity-50">
          {saving ? "Adding..." : "Add to Timeline"}
        </button>
      </form>
    </div>
  );
}
